import { Address, FilledAddress, isDefined } from '@aloft/toolkit-shared';
import { UseQueryResult } from '@tanstack/react-query';

import { formatReferenceViewID } from '../../modules/referenceView/referenceViewID/formatReferenceViewID.ts';
import { selectCompSequence } from '../../redux/features/selectors/orderSelectors.ts';
import { selectCraftsmanMetadataById } from '../../redux/features/selectors/selectCraftsmanMetadataById.ts';
import { useAppSelector } from '../../redux/hooks.ts';
import { ListingResult } from './listings/types.ts';
import { useGetListings } from './listings/useGetListings.ts';
import { ListingIDsByPropertyID, useListingIDsByPropertyID } from './useListingIDsByPropertyID.tsx';

const fillAddressFields = ({
  AddressLineText,
  CityName,
  StateCode,
  PostalCode,
  unitNumber,
  CountyName,
}: Address): FilledAddress => ({
  AddressLineText: AddressLineText ?? '',
  CityName: CityName ?? '',
  StateCode: StateCode ?? '',
  PostalCode: PostalCode ?? '',
  unitNumber: unitNumber ?? '',
  CountyName: CountyName ?? '',
});

const getAddressForProperty = (
  address: Address | undefined,
  listingIDsByPropertyID: ListingIDsByPropertyID,
  listings: UseQueryResult<ListingResult>[],
  propertyID: string,
): FilledAddress | undefined => {
  if (isDefined(address)) return fillAddressFields(address);

  const listingData = listings.find(
    (result) =>
      result.status === 'success' &&
      result.data &&
      'listing' in result.data &&
      result.data.listing &&
      result.data.listing.ToolkitListingID === listingIDsByPropertyID[propertyID],
  );

  if (isDefined(listingData) && listingData.data?.status === 'found') {
    const { UnparsedAddress, City, StateOrProvince, PostalCode, UnitNumber, CountyOrParish } =
      listingData.data.listing;

    return fillAddressFields({
      AddressLineText: UnparsedAddress,
      CityName: City,
      StateCode: StateOrProvince,
      PostalCode,
      unitNumber: UnitNumber,
      CountyName: CountyOrParish,
    });
  }

  return undefined;
};

export function usePublicRecordsForCraftsmanOrderArgs() {
  const craftsmanMetadataById = useAppSelector(selectCraftsmanMetadataById);
  const compSequence = useAppSelector(selectCompSequence);
  const listingIds = Object.values(craftsmanMetadataById)
    .map(({ listingID }) => listingID)
    .filter(isDefined);

  const listingIDsByPropertyID = useListingIDsByPropertyID();
  const listings = useGetListings(listingIds);

  const addressesByID = compSequence.reduce<Record<string, FilledAddress>>((acc, compID) => {
    const address = getAddressForProperty(
      craftsmanMetadataById[formatReferenceViewID({ type: 'comparable', compID })]?.address,
      listingIDsByPropertyID,
      listings,
      compID,
    );

    if (isDefined(address)) acc[compID] = address;
    return acc;
  }, {});

  const subjectAddress = getAddressForProperty(
    craftsmanMetadataById[formatReferenceViewID({ type: 'subject' })]?.address,
    listingIDsByPropertyID,
    listings,
    'subject',
  );

  if (subjectAddress) addressesByID.subject = subjectAddress;

  return {
    listings,
    addressesByID,
  };
}
